import React, { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import api from '../api';

export default function SalesReport() {
  const [from, setFrom] = useState(dayjs().startOf('month').format('YYYY-MM-DD'));
  const [to, setTo] = useState(dayjs().endOf('month').format('YYYY-MM-DD'));
  const [sales, setSales] = useState([]);
  const [settings, setSettings] = useState({});
  const [customers, setCustomers] = useState([]);
  const [filterCustomer, setFilterCustomer] = useState('');
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get('/settings/get').then(res => setSettings(res.data || {}));
    api.get('/customers').then(res => setCustomers(res.data || []));
  }, []);

  useEffect(() => {
    fetchData();
  }, [from, to]);

  const fetchData = async () => {
    const res = await api.get('/sales-reports', { params: { from, to } });
    setSales(res.data.sales || []);
  };

  const handleThisMonth = () => {
    setFrom(dayjs().startOf('month').format('YYYY-MM-DD'));
    setTo(dayjs().endOf('month').format('YYYY-MM-DD'));
  };

  const calculateSums = inv => {
    let taxable = 0, cgst = 0, sgst = 0, igst = 0, csamt = 0;
    if (inv.items?.length) {
      inv.items.forEach(i => {
        taxable += +i.Amount || 0;
        cgst += +i.CGST || 0;
        sgst += +i.SGST || 0;
        igst += +i.IGST || 0;
        csamt += +i.Cess || 0;
      });
    } else {
      taxable = (+inv.Taxable || +inv.Total || 0) - (+inv.Discount || 0);
      cgst = +inv.TotalCGST || +inv.CGST || 0;
      sgst = +inv.TotalSGST || +inv.SGST || 0;
      igst = +inv.IGST || 0;
      csamt = +inv.Cess || 0;
    }
    const total = +inv.Total || (taxable + cgst + sgst + igst + csamt);
    return { taxable, cgst, sgst, igst, csamt, total };
  };

  const filteredSales = sales.filter(inv =>
    !filterCustomer || inv.CustomerName === filterCustomer
  );

  const grandTotals = filteredSales.reduce((acc, inv) => {
    const s = calculateSums(inv);
    acc.taxable += s.taxable;
    acc.cgst += s.cgst;
    acc.sgst += s.sgst;
    acc.igst += s.igst;
    acc.csamt += s.csamt;
    acc.total += s.total;
    return acc;
  }, { taxable: 0, cgst: 0, sgst: 0, igst: 0, csamt: 0, total: 0 });

  const sendEmailReport = async () => {
    setSending(true);
    try {
      await api.post('/send-sales-report', {
        email,
        from,
        to,
        company: settings,
        sales: filteredSales
      });
      alert('Email sent!');
      setShowEmailModal(false);
    } catch (err) {
      console.error(err);
      alert('Failed to send email');
    } finally {
      setSending(false);
    }
  };

  const cardColors = ['primary', 'success', 'info', 'warning', 'danger', 'dark'];
  const keys = ['taxable', 'cgst', 'sgst', 'igst', 'csamt', 'total'];

  return (
    <div className="container py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h3 className="fw-bold mb-0 text-primary">Sales Report</h3>
        <button className="btn btn-dark" onClick={() => setShowEmailModal(true)}>Email</button>
      </div>

      {/* Filters */}
      <div className="row g-3 align-items-end mb-3">
        <div className="col-md-2">
          <label className="form-label">From</label>
          <input type="date" className="form-control" value={from} onChange={e => setFrom(e.target.value)} />
        </div>
        <div className="col-md-2">
          <label className="form-label">To</label>
          <input type="date" className="form-control" value={to} onChange={e => setTo(e.target.value)} />
        </div>
        <div className="col-md-4">
          <label className="form-label">Customer</label>
          <select className="form-select" value={filterCustomer} onChange={e => setFilterCustomer(e.target.value)}>
            <option value="">All</option>
            {customers.map(c => (
              <option key={c.CustomerID} value={c.Name}>{c.Name}</option>
            ))}
          </select>
        </div>
        <div className="col-md-2">
          <button className="btn btn-outline-primary w-100" onClick={fetchData}>Get Report</button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="row mb-4">
        {keys.map((key, i) => (
          <div className="col-md-2 mb-2" key={key}>
            <div className={`card text-white bg-${cardColors[i]} shadow`}>
              <div className="card-body p-2 text-center">
                <div className="small">{key.toUpperCase()}</div>
                <h5 className="mb-0">₹{grandTotals[key].toFixed(2)}</h5>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Table */}
      <div className="table-responsive">
        <table className="table table-bordered table-striped align-middle">
          <thead className="table-dark">
            <tr>
              <th>#</th><th>Invoice No</th><th>Date</th><th>Customer</th><th>GSTIN</th>
              <th>Taxable</th><th>CGST</th><th>SGST</th><th>IGST</th><th>Cess</th><th>Total</th>
            </tr>
          </thead>
          <tbody>
            {filteredSales.map((inv, i) => {
              const s = calculateSums(inv);
              return (
                <tr key={inv.InvoiceID}>
                  <td>{i + 1}</td>
                  <td>{inv.InvoiceNo}</td>
                  <td>{dayjs(inv.InvoiceDate).format('DD/MM/YYYY')}</td>
                  <td>{inv.CustomerName}</td>
                  <td>{inv.CustomerGSTIN || ''}</td>
                  <td>₹{s.taxable.toFixed(2)}</td>
                  <td>₹{s.cgst.toFixed(2)}</td>
                  <td>₹{s.sgst.toFixed(2)}</td>
                  <td>₹{s.igst.toFixed(2)}</td>
                  <td>₹{s.csamt.toFixed(2)}</td>
                  <td>₹{s.total.toFixed(2)}</td>
                </tr>
              );
            })}
            {!filteredSales.length && (
              <tr><td colSpan={11} className="text-center">No records found.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Email Modal */}
      {showEmailModal && (
        <div className="modal d-block">
          <div className="modal-dialog modal-sm">
            <div className="modal-content">
              <form onSubmit={e => { e.preventDefault(); sendEmailReport(); }}>
                <div className="modal-header">
                  <h5 className="modal-title">Send Report</h5>
                  <button type="button" className="btn-close" onClick={() => setShowEmailModal(false)} />
                </div>
                <div className="modal-body">
                  <label>Email address</label>
                  <input
                    type="email"
                    className="form-control"
                    required
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                  />
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowEmailModal(false)}>Cancel</button>
                  <button type="submit" className="btn btn-primary" disabled={sending}>
                    {sending ? 'Sending…' : 'Send'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
